import React, { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "components/ConfirmDialog";
import SideDrawer from "components/SideDrawer";
import StatusBadge, { BadgeTone } from "components/StatusBadge";
import { useToast } from "components/Toast";
import {
  ESIM_STATUS_TONES,
  EsimOrder,
  getEsimOrder,
  orderAccount,
  orderCostNgn,
  orderCustomerName,
  orderDate,
  orderPackage,
  orderPhone,
  orderTransaction,
  retryEsimOrder,
} from "services/esim";
import { formatNaira, getApiError } from "services/finance";
import { formatDisplayDate, formatUsd } from "utils/helpers";
import { RequestRefundDrawer, ResolveRefundDrawer } from "./EsimRefundDrawers";

export const CAN_RETRY = ["failed", "processing"];
export const CAN_REQUEST_REFUND = ["completed", "failed"];
export const CAN_RESOLVE_REFUND = ["refund_requested"];

const PAYMENT_TONES: Record<string, BadgeTone> = {
  success: "success",
  successful: "success",
  pending: "pending",
  failed: "error",
};
const FINANCE_TONES: Record<string, BadgeTone> = { posted: "success", pending: "pending" };

const humanize = (value?: string | null) => (value ? value.replace(/[-_]/g, " ") : "—");
const yesNo = (value?: boolean) => (value === undefined ? "—" : value ? "Yes" : "No");

export const CopyValue = ({ value, mono }: { value?: string; mono?: boolean }) => {
  const { showToast } = useToast();
  if (!value) return <>—</>;
  return (
    <Box component="span" display="inline-flex" alignItems="center" gap="2px" minWidth={0}>
      <Box
        component="span"
        sx={{ wordBreak: "break-all", fontFamily: mono ? "monospace" : undefined, fontSize: mono ? 13 : undefined }}
      >
        {value}
      </Box>
      <Tooltip title="Copy">
        <IconButton
          size="small"
          aria-label="Copy"
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard?.writeText(value);
            showToast("Copied", "info");
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 14, color: "#98A2B3" }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

// Like SummaryRow, but a div so badges and copy buttons can sit in the value.
const Row = ({ label, value, bold }: { label: string; value: React.ReactNode; bold?: boolean }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" gap="12px" py="7px">
    <Typography fontSize={13} color="#667085" flexShrink={0}>
      {label}
    </Typography>
    <Box
      fontSize={13}
      fontWeight={bold ? 700 : 500}
      color="#101828"
      textAlign="right"
      minWidth={0}
      sx={{ textTransform: "none" }}
    >
      {value}
    </Box>
  </Box>
);

const Card = ({ title, action, children }: { title?: string; action?: React.ReactNode; children: React.ReactNode }) => (
  <Box border="1px solid #EAECF0" borderRadius="12px" p="14px 16px" bgcolor="#FFFFFF">
    {title && (
      <Box display="flex" justifyContent="space-between" alignItems="center" mb="4px">
        <Typography fontSize={12} fontWeight={600} color="#475467" letterSpacing="0.04em" textTransform="uppercase">
          {title}
        </Typography>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box flex={1} minWidth={0} bgcolor="rgba(255,255,255,0.14)" borderRadius="10px" p="10px 12px">
    <Typography fontSize={11} sx={{ opacity: 0.8 }}>
      {label}
    </Typography>
    <Typography fontSize={15} fontWeight={700} noWrap>
      {value}
    </Typography>
  </Box>
);

interface Step {
  label: string;
  at?: string | null;
  tone: "done" | "error" | "pending";
  note?: string;
}

const STEP_COLORS = { done: "#12B76A", error: "#F04438", pending: "#D0D5DD" };

const Timeline = ({ steps }: { steps: Step[] }) => (
  <Box>
    {steps.map((step, i) => (
      <Box key={step.label} display="flex" gap="12px">
        <Box display="flex" flexDirection="column" alignItems="center" pt="4px">
          <Box width={10} height={10} borderRadius="50%" bgcolor={STEP_COLORS[step.tone]} flexShrink={0} />
          {i < steps.length - 1 && <Box width="2px" flex={1} bgcolor="#EAECF0" my="4px" />}
        </Box>
        <Box pb={i < steps.length - 1 ? "14px" : 0} minWidth={0}>
          <Typography fontSize={13} fontWeight={600} color={step.tone === "pending" ? "#98A2B3" : "#101828"}>
            {step.label}
          </Typography>
          <Typography fontSize={12} color="#667085">
            {step.at ? formatDisplayDate(step.at) : "Not yet"}
          </Typography>
          {step.note && (
            <Typography fontSize={12} color="#B42318" mt="2px" sx={{ wordBreak: "break-word" }}>
              {step.note}
            </Typography>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

interface EsimOrderPanelProps {
  order: EsimOrder;
  onClose: () => void;
}

const EsimOrderPanel = ({ order: listRow, onClose }: EsimOrderPanelProps) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [confirmRetry, setConfirmRetry] = useState(false);
  const [drawer, setDrawer] = useState<"request" | "resolve" | null>(null);
  const ref = listRow.order_ref;

  // The list row renders straight away; the detail call fills in anything it adds.
  const { data: detail } = useQuery({
    queryKey: ["esimOrder", ref],
    queryFn: () => getEsimOrder(ref),
  });
  const order: EsimOrder = { ...listRow, ...(detail || {}) };

  const retry = useMutation({
    mutationFn: () => retryEsimOrder(ref),
    onSuccess: (res) => {
      const status = res?.data?.status;
      // A retry can come back still processing; that's progress, not failure.
      showToast(
        res?.message || "Retry started",
        status === "completed" ? "success" : status === "failed" ? "error" : "info",
      );
      setConfirmRetry(false);
      queryClient.invalidateQueries({ queryKey: ["esimOrder", ref] });
      queryClient.invalidateQueries({ queryKey: ["esimOrders"] });
    },
    onError: (err) => {
      showToast(getApiError(err, "Retry failed"), "error");
      setConfirmRetry(false);
    },
  });

  const status = order.status || "";
  const pkg = orderPackage(order);
  const operator = pkg.operator;
  const sim = order.sims?.[0];
  const txn = orderTransaction(order);
  const account = orderAccount(order);
  const customerName = orderCustomerName(order);
  const accountName = [account?.firstName, account?.lastName].filter(Boolean).join(" ");
  const cost = orderCostNgn(order);
  const margin = cost !== undefined && order.total_price ? order.total_price - (order.charges || 0) - cost : undefined;
  const failed = status === "failed";

  const steps: Step[] = [
    { label: "Order created", at: orderDate(order), tone: "done" },
    {
      label: "Payment received",
      at: txn?.transactionDate,
      tone: txn?.transactionDate ? "done" : "pending",
    },
    {
      label: failed ? "Fulfilment failed" : "eSIM issued",
      at: order.completedAt,
      tone: failed ? "error" : order.completedAt ? "done" : "pending",
      note: order.last_error || undefined,
    },
  ];

  const networks = (operator?.coverages || []).flatMap((c) =>
    (c.networks || []).map((n) => ({ ...n, country: c.name || c.code })),
  );

  const actions = (
    <>
      {CAN_RETRY.includes(status) && (
        <Button variant="contained" size="small" onClick={() => setConfirmRetry(true)} sx={{ textTransform: "none" }}>
          Retry fulfilment
        </Button>
      )}
      {CAN_REQUEST_REFUND.includes(status) && (
        <Button variant="outlined" size="small" onClick={() => setDrawer("request")} sx={{ textTransform: "none" }}>
          Request refund
        </Button>
      )}
      {CAN_RESOLVE_REFUND.includes(status) && (
        <Button variant="contained" size="small" onClick={() => setDrawer("resolve")} sx={{ textTransform: "none" }}>
          Resolve refund
        </Button>
      )}
    </>
  );

  return (
    <SideDrawer open onClose={onClose} title={order.kind === "topup" ? "Top-up order" : "eSIM order"} width={540}>
      {/* Hero: operator brand colours, like the card the customer sees */}
      <Box
        borderRadius="16px"
        p="18px"
        color="#FFFFFF"
        sx={{
          background: `linear-gradient(135deg, ${operator?.gradient_start || "#1D3B2A"}, ${
            operator?.gradient_end || "#0b1c12"
          })`,
        }}
      >
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap="12px">
          <Box display="flex" alignItems="center" gap="12px" minWidth={0}>
            {pkg.destination_image && (
              <Box
                component="img"
                src={pkg.destination_image}
                alt=""
                width={44}
                height={33}
                borderRadius="6px"
                flexShrink={0}
                sx={{ objectFit: "cover", boxShadow: "0 0 0 2px rgba(255,255,255,0.35)" }}
              />
            )}
            <Box minWidth={0}>
              <Typography fontSize={18} fontWeight={700} noWrap>
                {pkg.destination_title || "Unknown destination"}
              </Typography>
              <Typography fontSize={13} sx={{ opacity: 0.85 }} noWrap>
                {[operator?.title, pkg.title].filter(Boolean).join(" · ") || "—"}
              </Typography>
            </Box>
          </Box>
          <Box bgcolor="#FFFFFF" borderRadius="8px" flexShrink={0}>
            <StatusBadge status={status} tones={ESIM_STATUS_TONES} />
          </Box>
        </Box>
        <Box display="flex" gap="8px" mt="16px">
          <Stat label="Data" value={pkg.is_unlimited ? "Unlimited" : pkg.data || "—"} />
          <Stat label="Validity" value={pkg.day ? `${pkg.day} days` : "—"} />
          <Stat label="Paid" value={formatNaira(order.total_price)} />
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mt="12px" gap="8px" flexWrap="wrap">
        <Typography component="div" fontSize={13} color="#475467">
          <CopyValue value={ref} mono />
        </Typography>
        <Typography fontSize={12} color="#667085">
          {formatDisplayDate(orderDate(order))}
        </Typography>
      </Box>

      <Box display="flex" gap="8px" flexWrap="wrap" alignItems="center" mt="12px">
        {actions}
        <Button
          size="small"
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          onClick={() => navigate(`/esim/orders/${encodeURIComponent(ref)}`)}
          sx={{ textTransform: "none", ml: "auto" }}
        >
          Ledger & top-ups
        </Button>
      </Box>

      {failed && (
        <Alert severity="error" sx={{ mt: 1.5, borderRadius: "10px" }}>
          The customer paid but the eSIM couldn't be issued. Check the Airalo balance, then retry or refund.
        </Alert>
      )}
      {status === "processing" && (order.fulfilment_attempts || 0) > 1 && (
        <Alert severity="warning" sx={{ mt: 1.5, borderRadius: "10px" }}>
          {order.fulfilment_attempts} fulfilment attempts so far.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{
          mt: 2,
          mb: 2,
          minHeight: 40,
          borderBottom: "1px solid #EAECF0",
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 14, minHeight: 40, px: 1.5 },
        }}
      >
        <Tab label="Overview" />
        <Tab label="Install" />
        <Tab label="Network" />
        <Tab label="Pricing" />
      </Tabs>

      <Box display="flex" flexDirection="column" gap="12px" pb="8px">
        {tab === 0 && (
          <>
            <Card title="Customer">
              <Box display="flex" alignItems="center" gap="12px" py="8px">
                <Avatar sx={{ bgcolor: "#ECFDF3", color: "#027A48", fontSize: 15, fontWeight: 700 }}>
                  {initials(customerName || order.email || "")}
                </Avatar>
                <Box minWidth={0}>
                  <Typography fontSize={15} fontWeight={600} color="#101828" noWrap>
                    {customerName || "—"}
                  </Typography>
                  <Typography fontSize={13} color="#667085" noWrap>
                    {order.email || "—"}
                  </Typography>
                </Box>
              </Box>
              <Row label="Email" value={<CopyValue value={order.email} />} />
              <Row label="Phone" value={<CopyValue value={orderPhone(order)} />} />
              {account && (
                <Row
                  label="Account"
                  value={
                    <Box>
                      <Box>{accountName || "—"}</Box>
                      {account.email !== order.email && (
                        <Typography fontSize={12} color="#667085">
                          {account.email}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              )}
            </Card>

            <Card
              title="Payment"
              action={txn?.status && <StatusBadge status={txn.status} tones={PAYMENT_TONES} />}
            >
              {txn ? (
                <>
                  <Row label="Amount" value={formatNaira(txn.amount)} bold />
                  <Row label="Bank" value={txn.bankName || "—"} />
                  <Row
                    label="Account"
                    value={<CopyValue value={[txn.accountNumber, txn.accountName].filter(Boolean).join(" · ")} />}
                  />
                  <Row label="Paid on" value={formatDisplayDate(txn.transactionDate)} />
                  {(txn.transactionReference || txn.paymentReference) && (
                    <Row
                      label="Reference"
                      value={<CopyValue value={txn.transactionReference || txn.paymentReference} />}
                    />
                  )}
                </>
              ) : (
                <Typography fontSize={13} color="#667085" py="6px">
                  No payment recorded on this order.
                </Typography>
              )}
            </Card>

            <Card title="Timeline">
              <Box pt="8px">
                <Timeline steps={steps} />
              </Box>
              {(order.fulfilment_attempts ?? 0) > 0 && (
                <Typography fontSize={12} color="#667085" mt="12px">
                  {order.fulfilment_attempts} fulfilment attempt{order.fulfilment_attempts === 1 ? "" : "s"}
                  {order.alerts_sent?.length ? ` · ${order.alerts_sent.length} usage alert(s) sent` : ""}
                </Typography>
              )}
            </Card>
          </>
        )}

        {tab === 1 &&
          (sim ? (
            <>
              <Card>
                <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} alignItems="center" gap="16px">
                  {sim.qrcode_url && (
                    <Box
                      component="img"
                      src={sim.qrcode_url}
                      alt="eSIM QR code"
                      width={148}
                      height={148}
                      p="6px"
                      border="1px solid #EAECF0"
                      borderRadius="10px"
                      flexShrink={0}
                    />
                  )}
                  <Box display="flex" flexDirection="column" gap="8px" minWidth={0} width="100%">
                    <Typography fontSize={14} fontWeight={600} color="#101828">
                      Scan to install
                    </Typography>
                    <Typography fontSize={12} color="#667085">
                      Or send the customer the manual details below.
                      {operator?.install_window_days
                        ? ` Must be installed within ${operator.install_window_days} days.`
                        : ""}
                    </Typography>
                    <Box display="flex" gap="8px" flexWrap="wrap">
                      {sim.direct_apple_installation_url && (
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                          href={sim.direct_apple_installation_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: "none" }}
                        >
                          iOS install link
                        </Button>
                      )}
                      {sim.qrcode && (
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                          onClick={() => {
                            navigator.clipboard?.writeText(sim.qrcode || "");
                            showToast("LPA string copied", "info");
                          }}
                          sx={{ textTransform: "none" }}
                        >
                          Copy LPA string
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Card>

              <Card title="Manual install">
                <Row label="ICCID" value={<CopyValue value={sim.iccid} mono />} />
                <Row label="SM-DP+ address" value={<CopyValue value={sim.lpa} mono />} />
                <Row label="Activation code" value={<CopyValue value={sim.matching_id} mono />} />
                {sim.confirmation_code && (
                  <Row label="Confirmation code" value={<CopyValue value={sim.confirmation_code} mono />} />
                )}
              </Card>

              <Card title="APN">
                {sim.apn?.ios || sim.apn?.android ? (
                  (["ios", "android"] as const).map((os) =>
                    sim.apn?.[os] ? (
                      <Row
                        key={os}
                        label={os === "ios" ? "iOS" : "Android"}
                        value={`${humanize(sim.apn[os]?.apn_type)} · ${sim.apn[os]?.apn_value || "—"}`}
                      />
                    ) : null,
                  )
                ) : (
                  <Row label="APN" value={`${humanize(sim.apn_type)} · ${sim.apn_value || "—"}`} />
                )}
                <Row label="Data roaming" value={sim.is_roaming ? "Must be ON" : "Not needed"} />
              </Card>
            </>
          ) : (
            <Card>
              <Typography fontSize={13} color="#667085" py="16px" textAlign="center">
                {failed ? "No eSIM was issued for this order." : "The eSIM hasn't been issued yet."}
              </Typography>
            </Card>
          ))}

        {tab === 2 && (
          <>
            {operator?.warning && (
              <Alert severity="warning" sx={{ borderRadius: "10px" }}>
                {operator.warning}
              </Alert>
            )}
            <Card title="Coverage">
              {networks.length ? (
                networks.map((n) => (
                  <Box key={`${n.country}-${n.name}`} display="flex" justifyContent="space-between" alignItems="center" py="7px">
                    <Typography fontSize={13} color="#101828" fontWeight={500}>
                      {n.name}
                      <Typography component="span" fontSize={12} color="#667085">
                        {" "}
                        · {n.country}
                      </Typography>
                    </Typography>
                    <Box display="flex" gap="4px">
                      {(n.types || []).map((t) => (
                        <Chip
                          key={t}
                          label={t}
                          size="small"
                          sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "#EFF8FF", color: "#175CD3" }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography fontSize={13} color="#667085" py="6px">
                  No coverage details.
                </Typography>
              )}
            </Card>

            <Card title="Plan rules">
              <Row label="Operator" value={operator?.title || "—"} />
              <Row label="SIM type" value={[operator?.esim_type, humanize(operator?.plan_type)].filter(Boolean).join(" · ")} />
              <Row label="Activates on" value={humanize(operator?.activation_policy)} />
              <Row label="Rechargeable" value={yesNo(operator?.rechargeability)} />
              <Row label="KYC required" value={yesNo(operator?.is_kyc_verify)} />
              {operator?.topup_grace_window_days !== undefined && (
                <Row label="Top-up grace" value={`${operator.topup_grace_window_days} days`} />
              )}
              <Row label="Package ID" value={<CopyValue value={pkg.package_id} mono />} />
            </Card>

            {(operator?.info?.length || operator?.other_info) && (
              <Card title="Notes">
                <Box component="ul" m={0} pl="18px" py="6px">
                  {(operator?.info || []).map((line) => (
                    <Typography component="li" key={line} fontSize={13} color="#344054" py="2px">
                      {line}
                    </Typography>
                  ))}
                </Box>
                {operator?.other_info && (
                  <Typography fontSize={13} color="#344054" mt="4px">
                    {operator.other_info}
                  </Typography>
                )}
              </Card>
            )}
          </>
        )}

        {tab === 3 && (
          <>
            <Card title="Customer price">
              <Row label="Retail price" value={order.price_usd !== undefined ? formatUsd(order.price_usd) : "—"} />
              <Row label="Rate at order" value={order.usd_ngn_rate ? `${formatNaira(order.usd_ngn_rate)} / $1` : "—"} />
              <Row label="Markup" value={order.markup_percent !== undefined ? `${order.markup_percent}%` : "—"} />
              <Row label="Charges" value={formatNaira(order.charges)} />
              <Box borderTop="1px dashed #EAECF0" mt="4px" />
              <Row label="Total paid" value={formatNaira(order.total_price)} bold />
            </Card>

            <Card title="Cost & margin">
              <Row
                label="Airalo cost"
                value={
                  order.airalo_net_price_usd ?? order.net_price_usd
                    ? `${formatUsd(order.airalo_net_price_usd ?? order.net_price_usd)}${
                        cost !== undefined ? ` · ${formatNaira(Math.round(cost))}` : ""
                      }`
                    : "—"
                }
              />
              {margin !== undefined && (
                <Row
                  label="Est. margin"
                  bold
                  value={
                    <Box component="span" color={margin >= 0 ? "#027A48" : "#B42318"}>
                      {formatNaira(Math.round(margin))}
                      {order.total_price ? ` (${((margin / order.total_price) * 100).toFixed(1)}%)` : ""}
                    </Box>
                  }
                />
              )}
              <Typography fontSize={11} color="#98A2B3" mt="4px">
                Margin uses the rate at order time and excludes payment fees.
              </Typography>
            </Card>

            <Card title="References">
              <Row label="Airalo order" value={<CopyValue value={order.airalo_order_id?.toString()} mono />} />
              <Row label="Airalo code" value={<CopyValue value={order.airalo_code} mono />} />
              <Row
                label="Finance"
                value={
                  order.financeStatus ? <StatusBadge status={order.financeStatus} tones={FINANCE_TONES} /> : "—"
                }
              />
            </Card>
          </>
        )}
      </Box>

      <ConfirmDialog
        open={confirmRetry}
        title="Retry fulfilment?"
        message="This asks Airalo to issue the eSIM again. It checks Airalo first, so it won't buy a second eSIM."
        confirmLabel="Retry"
        loading={retry.isPending}
        onConfirm={() => retry.mutate()}
        onClose={() => setConfirmRetry(false)}
      />
      {drawer === "request" && <RequestRefundDrawer open onClose={() => setDrawer(null)} order={order} />}
      {drawer === "resolve" && <ResolveRefundDrawer open onClose={() => setDrawer(null)} order={order} />}
    </SideDrawer>
  );
};

export default EsimOrderPanel;
